import { FormatDate } from '@/client/components/format/format-date';
import { LocalizedText } from '@/client/components/localized-text';
import { DEADLINE_TZ } from '@/client/components/market-deadline';
import { useHomeMarketsPreview } from '@/client/hooks/use-home-markets-preview';
import { getBinaryProbabilityPercentage } from '@/client/pages/home/components/market-card/binary-options';
import { Market } from '@/libs/sdk/api';
import type { Time } from 'lightweight-charts';
import { memo, useMemo } from 'react';
import { Link } from 'wouter';

// Top marquee ticker (reference-site pattern): pure CSS, content rendered
// twice and translated -50% for a seamless loop. Items come from live
// market data -- no hardcoded events.
const TickerItem = ({ market }: { market: Market }) => {
  const probability = getBinaryProbabilityPercentage(market.options);
  if (probability === null) return null;
  return (
    <Link
      href={`/market/${market.marketAddress}`}
      className="flex items-center gap-2 px-4 py-1.5 mx-2 rounded-full bg-secondary/80 border border-border whitespace-nowrap text-xs hover:border-border-bright transition-colors"
    >
      <span className="text-foreground/90 font-medium max-w-56 truncate">
        <LocalizedText i18n={market.nameI18n}>{market.marketName}</LocalizedText>
      </span>
      <span className="text-muted-foreground">
        {/* SGT zone so the compact date matches the settlement day; a 16:00 UTC
            daily deadline is SGT midnight, so device-local rendering flips the day. */}
        <FormatDate time={Number(market.deadline) as Time} formatStr="MMM d" timeZone={DEADLINE_TZ} />
      </span>
      <span className="font-mono text-beneficial-foreground">{probability}¢</span>
      <span className="text-muted-foreground">/</span>
      <span className="font-mono text-destructive">{100 - probability}¢</span>
    </Link>
  );
};

export const TheTicker = memo(() => {
  // Shared home preview query (also used by search-recommend) -- scoped to
  // the global currency lens so the ¢ prices all share one collateral.
  const { data } = useHomeMarketsPreview();
  const markets = useMemo(() => (data?.pages[0]?.markets ?? []).filter(m => m.options?.length === 2), [data]);

  if (markets.length < 4) return null; // marquee needs enough width to loop cleanly

  return (
    <div className="w-full overflow-hidden border-b border-foreground/4 bg-background py-1.5" aria-hidden>
      <div className="ticker-track">
        {[0, 1].map(dup => (
          <div key={dup} className="flex items-center" aria-hidden={dup === 1}>
            {markets.map(m => (
              <TickerItem key={`${dup}-${m.marketAddress}`} market={m} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
});

TheTicker.displayName = 'TheTicker';
